import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactPlayer, { ReactPlayerProps } from 'react-player';
import { FETCH_ASSETS_URL } from 'appenv';

const PlayButton = `${FETCH_ASSETS_URL}/assets/play-button.png`;
const PlayIcon = <img src={PlayButton} alt="Play Button" />;

export interface ReactPlayerAutoplayProps {
  videoUrl: string;
  thumbnailUrl?: string;
  autoPlay?: boolean;
  defaultPropsOverride?: ReactPlayerProps;
  videoPlayCallback?: () => void;
  videoStopCallback?: () => void;
  displayed?: boolean;
  muted?: boolean;
}

const ReactPlayerAutoplay: FC<ReactPlayerAutoplayProps> = ({
  videoUrl,
  thumbnailUrl,
  autoPlay = false,
  defaultPropsOverride = {},
  videoPlayCallback,
  videoStopCallback,
  displayed = true,
  muted = false,
}) => {
  const playerRef = useRef<ReactPlayer>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [isReady, setReady] = useState(false);

  const onPlay = useCallback(() => {
    setPlaying(true);
    if (videoPlayCallback) videoPlayCallback();
  }, [videoPlayCallback]);

  const onPause = useCallback(() => {
    setPlaying(false);
    if (videoStopCallback) videoStopCallback();
  }, [videoStopCallback]);

  const onReady = useCallback(() => setReady(true), []);

  const defaultPlayerProps = useMemo(() => ({
    light: autoPlay ? false : (thumbnailUrl ?? false),
    onPlay,
    onPause,
    onReady,
    url: videoUrl,
    controls: true,
    playIcon: PlayIcon,
    width: '100%',
    height: '100%',
    config: {
      file: {
        attributes: {
          playsInline: true,
          disablePictureInPicture: true,
          controlsList: 'nodownload',
        },
      },
    },
    playsinline: true,
    muted,
    style: {
      zIndex: 100,
      backgroundColor: 'black',
      padding: '0 !important',
    },
    ...defaultPropsOverride,
  } as ReactPlayerProps), [
    autoPlay,
    thumbnailUrl,
    muted,
    onPlay,
    onPause,
    onReady,
    videoUrl,
    defaultPropsOverride,
  ]);

  useEffect(() => {
    const playVideo = async (videoElement: HTMLVideoElement) => {
      try {
        await videoElement.play();
      } catch (e) {
        console.warn('Failed to autoplay... Playing with mute...');
        videoElement.muted = true;
        await videoElement.play();
      } finally {
        setReady(false);
      }
    };

    if (isReady && playerRef.current && displayed) {
      const wrapperElement: HTMLDivElement = (playerRef.current as any).wrapper;
      const videoElement = wrapperElement.firstElementChild as HTMLVideoElement;
      if (autoPlay) playVideo(videoElement);
    }
  }, [autoPlay, isReady, displayed]);

  /**
   * Explanation:
   * The react-slick package achieves its "infinite" effect via cloning child elements.
   * This means that there might be more than 1 ReactPlayer per video asset.
   * Pausing the displayed ReactPlayer will not pause the cloned ones.
   * ---
   * Previously, JJ used a video state map to broadcast a video's playing state to both the default and cloned videos.
   * However, the videos didn't play / pause at the exact same moment. This caused weird audio issues.
   * ---
   * Cloned elements by react-slick will have the slick-cloned class.
   * Below is the fix for it.
   */
  const playing = useMemo(() => {
    const wrapperElement: HTMLDivElement = (playerRef.current as any)?.wrapper;
    if (!wrapperElement) return false;
    const slickContainer = wrapperElement.parentElement.parentElement.parentElement;
    if (slickContainer.classList.contains('slick-cloned')) {
      const videoElement = wrapperElement.firstElementChild as HTMLVideoElement;
      videoElement.muted = true;
    }
    return isPlaying && displayed;
  }, [isPlaying, displayed]);

  return (
    <ReactPlayer
      {...defaultPlayerProps}
      playing={playing}
      ref={playerRef}
    />
  );
};

export default ReactPlayerAutoplay;
